"use client";

import { useCallback } from "react";
import { useLocale, useTranslations } from "use-intl";

import { GlobeIcon, PersonLocationIcon } from "@/components/appIcons";
import { CountryFlag } from "@/components/country-flag";
import { formatCountry } from "@garimpa/lib/countries";

import { FacetSelect } from "./facet-select";

const INPUT_CLASS =
  "w-full px-3 py-2 text-sm bg-secondary border border-border rounded-md focus-within:outline-none focus-within:ring-1 focus-within:ring-primary cursor-pointer";

interface FacetCount {
  value: string;
  count: number;
}

export function SearchCountryFilters({
  music,
  country,
  origin,
  countryCounts,
  artistCountryCounts,
  updateFilter,
}: {
  music: boolean;
  country: string | undefined;
  origin: string | undefined;
  countryCounts: FacetCount[];
  artistCountryCounts: FacetCount[];
  updateFilter: (key: string, value: string | number | boolean | undefined) => void;
}) {
  const t = useTranslations("globals.search");
  const locale = useLocale();
  const renderCountryLabel = useCallback(
    (value: string) => (
      <span className="flex items-center gap-2">
        <CountryFlag code={value} decorative />
        {formatCountry(value, locale) || value}
      </span>
    ),
    [locale],
  );
  const matchesCountrySearch = useCallback(
    (item: FacetCount, query: string) => {
      if (item.value.toLowerCase().includes(query)) return true;
      return formatCountry(item.value, locale).toLowerCase().includes(query);
    },
    [locale],
  );
  return (
    <>
      {music && (
        <>
          <div>
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
              <PersonLocationIcon className="text-sm" /> {t("artistFrom")}
            </h3>
            <FacetSelect
              items={artistCountryCounts}
              value={origin}
              onChange={(value) => updateFilter("origin", value)}
              placeholder={t("artistCountryPlaceholder")}
              className={INPUT_CLASS}
              renderLabel={renderCountryLabel}
              matchesSearch={matchesCountrySearch}
            />
          </div>
          <hr className="border-border" />
        </>
      )}
      <div>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <GlobeIcon className="text-sm" /> {t("releasedIn")}
        </h3>
        <FacetSelect
          items={countryCounts}
          value={country}
          onChange={(value) => updateFilter("country", value)}
          placeholder={t("countryPlaceholder")}
          className={INPUT_CLASS}
          renderLabel={renderCountryLabel}
          matchesSearch={matchesCountrySearch}
        />
      </div>
    </>
  );
}
